import type { RunItemStatus, TestRunDto, TestRunItemDto } from '@tms/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Bot, CheckCircle2, ChevronLeft, ChevronRight, History, MessageSquare, Pencil, RotateCw, Save, SkipForward } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { RichText, RichTextEditor, toPlainText } from '../../components/rich-text';
import {
  Button,
  buttonClassName,
  Card,
  CardBody,
  CardHeader,
  CollapsibleCard,
  ConfirmDialog,
  CopyKey,
  ErrorState,
  LabelChip,
  ProgressBar,
  ResultBar,
  RunItemStatusBadge,
  RunItemStatusIcon,
  RunStatusBadge,
  SkeletonRows,
  Stat,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';
import { usePermissions } from '../auth/permissions';
import { EditRunDialog } from './EditRunDialog';
import { RunAutotests } from './RunAutotests';
import { RunItemHistoryDialog } from './RunItemHistoryDialog';
import {
  useBulkSetRunItemStatus,
  useCompleteTestRun,
  useReopenTestRun,
  useRerunTestRun,
  useRunAutomation,
  useSetRunItemStatus,
  useTestRun,
} from './api';

/** Result buttons, in the order the left and right arrows walk them. */
const STATUS_ACTIONS: Array<{ status: RunItemStatus; className: string }> = [
  { status: 'passed', className: 'bg-pass-soft text-pass hover:brightness-95' },
  { status: 'failed', className: 'bg-fail-soft text-fail hover:brightness-95' },
  { status: 'blocked', className: 'bg-block-soft text-block hover:brightness-95' },
  { status: 'skipped', className: 'bg-skip-soft text-skip hover:brightness-95' },
];

export const RunDetailPage = ({
  projectCode,
  runKey,
}: {
  projectCode: string;
  runKey: string;
}) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data: run, isPending, error, refetch } = useTestRun(projectCode, runKey);

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending || !run) return <SkeletonRows rows={6} className="p-0" />;

  return (
    <RunDetail
      projectCode={projectCode}
      run={run}
      navigate={navigate}
      canExecute={can('run:execute')}
      canManage={can('run:manage')}
      canCreate={can('run:create')}
      canStartAutotests={can('run:execute') && can('automation:run')}
    />
  );
};

interface RunDetailProps {
  projectCode: string;
  run: TestRunDto;
  navigate: ReturnType<typeof useNavigate>;
  canExecute: boolean;
  canManage: boolean;
  canCreate: boolean;
  canStartAutotests: boolean;
}

const RunDetail = ({
  projectCode,
  run,
  navigate,
  canExecute,
  canManage,
  canCreate,
  canStartAutotests,
}: RunDetailProps) => {
  const { t } = useT();
  const { dateTime } = useFormat();
  const { itemStatus } = useEnumLabels();
  const { errorText } = useErrorText();
  const setStatus = useSetRunItemStatus(projectCode, run.id);
  const bulkStatus = useBulkSetRunItemStatus(projectCode, run.id);
  const complete = useCompleteTestRun(projectCode, run.id);
  const reopen = useReopenTestRun(projectCode, run.id);
  const rerun = useRerunTestRun(projectCode);
  const queryClient = useQueryClient();
  const { data: automation } = useRunAutomation(projectCode, run.id);
  const autotested = useMemo(() => new Set(automation?.linkedItemIds), [automation]);

  // A finished pipeline wrote its results into the checks: show them.
  const appliedLaunches = automation?.launches.filter((launch) => launch.appliedAt).length ?? 0;
  const seenApplied = useRef<number | null>(null);
  useEffect(() => {
    if (seenApplied.current !== null && appliedLaunches > seenApplied.current) {
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.testRuns.all(projectCode), 'detail'],
      });
    }
    seenApplied.current = appliedLaunches;
  }, [appliedLaunches, projectCode, queryClient]);

  const [comments, setComments] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState(0);
  const [historyItem, setHistoryItem] = useState<TestRunItemDto | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const rowRefs = useRef(new Map<string, HTMLLIElement>());

  // Comments are seeded from the server and kept as local drafts while typing.
  useEffect(() => {
    setComments(Object.fromEntries(run.items.map((item) => [item.id, item.comment ?? ''])));
  }, [run.items]);

  const isActive = run.status === 'active';
  const editable = isActive && canExecute;
  const remaining = useMemo(
    () => run.items.filter((item) => item.status === 'not_run'),
    [run.items],
  );
  const failed = useMemo(() => run.items.filter((item) => item.status === 'failed'), [run.items]);

  const current = run.items[Math.min(selected, run.items.length - 1)];
  const currentComment = current ? (comments[current.id] ?? '') : '';
  const commentChanged = current ? currentComment.trim() !== (current.comment ?? '').trim() : false;

  const select = useCallback((index: number) => {
    setSelected(index);
  }, []);

  // Keep the current check visible while moving with the keyboard or buttons.
  // `scrollIntoView` is missing in some environments (jsdom), hence the guard.
  useEffect(() => {
    if (!current) return;
    rowRefs.current.get(current.id)?.scrollIntoView?.({ block: 'nearest' });
  }, [current]);

  const apply = useCallback(
    async (item: TestRunItemDto, status: RunItemStatus, advance = false) => {
      try {
        const draft = comments[item.id];
        await setStatus.mutateAsync({
          itemId: item.id,
          status,
          comment: draft?.trim() ? draft : null,
        });
        if (advance) {
          setSelected((index) => Math.min(index + 1, run.items.length - 1));
        }
      } catch (statusError) {
        toast.error(errorText(statusError, 'runs.resultFailed'));
      }
    },
    [comments, run.items.length, setStatus, errorText],
  );

  /**
   * Keyboard execution (§20): up and down walk the checks, left and right walk
   * the four results and record the one they land on — the way arrows move the
   * selection inside a radio group. Recording with an arrow stays on the check,
   * so the reader can change their mind without walking back.
   */
  useEffect(() => {
    if (!editable) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelected((index) => Math.min(index + 1, run.items.length - 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelected((index) => Math.max(index - 1, 0));
        return;
      }
      if ((event.key === 'ArrowRight' || event.key === 'ArrowLeft') && current) {
        event.preventDefault();
        const at = STATUS_ACTIONS.findIndex((action) => action.status === current.status);
        const next =
          event.key === 'ArrowRight'
            ? Math.min(at + 1, STATUS_ACTIONS.length - 1)
            : // Nothing recorded yet: the right arrow starts at the first result,
              // the left one at the last.
              at === -1
              ? STATUS_ACTIONS.length - 1
              : Math.max(at - 1, 0);
        const action = STATUS_ACTIONS[next];
        if (action && action.status !== current.status) void apply(current, action.status);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editable, run.items.length, current, apply]);

  const onComplete = async () => {
    try {
      await complete.mutateAsync(undefined);
      toast.success(t('runs.completed', { key: run.runKey }));
      setCompleteOpen(false);
    } catch (completeError) {
      toast.error(errorText(completeError, 'runs.completeFailed'));
    }
  };

  const onReopen = async () => {
    try {
      await reopen.mutateAsync(undefined);
      toast.success(t('runs.reopened', { key: run.runKey }));
    } catch (reopenError) {
      toast.error(errorText(reopenError, 'runs.reopenFailed'));
    }
  };

  const onRerun = async () => {
    try {
      const created = await rerun.mutateAsync({ runId: run.id, input: {} });
      toast.success(t('runs.rerunCreated', { key: created.runKey, source: run.runKey }));
      await navigate({
        to: '/projects/$projectCode/test-runs/$runKey',
        params: { projectCode, runKey: created.runKey },
      });
    } catch (rerunError) {
      toast.error(errorText(rerunError, 'runs.rerunFailed'));
    }
  };

  const skipRemaining = async () => {
    try {
      await bulkStatus.mutateAsync({
        itemIds: remaining.map((item) => item.id),
        status: 'skipped',
        comment: null,
      });
      toast.success(
        t('runs.skipped', { checks: t('runs.countChecks', { count: remaining.length }) }),
      );
    } catch (bulkError) {
      toast.error(errorText(bulkError, 'runs.skipFailed'));
    }
  };

  return (
    <>
      <PageHeader
        title={run.title}
        actions={
          <>
            {/* A button, not a quiet link: leaving the record is a real
                action and has to look like one. */}
            <Link
              to="/projects/$projectCode/test-runs"
              params={{ projectCode }}
              className={buttonClassName('secondary')}
            >
              <ArrowLeft className="size-3.5" />
              {t('runs.backToRuns')}
            </Link>
            {canManage && isActive ? (
              <Button onClick={() => setEditOpen(true)}>
                <Pencil className="size-3.5" />
                {t('common.edit')}
              </Button>
            ) : null}
            {canCreate ? (
              <Button onClick={() => void onRerun()} loading={rerun.isPending}>
                <RotateCw className="size-3.5" />
                {t('runs.rerun')}
              </Button>
            ) : null}
            {canManage ? (
              isActive ? (
                <Button variant="primary" onClick={() => setCompleteOpen(true)}>
                  <CheckCircle2 className="size-4" />
                  {t('runs.complete')}
                </Button>
              ) : (
                <Button onClick={() => void onReopen()} loading={reopen.isPending}>
                  {t('runs.reopen')}
                </Button>
              )
            ) : null}
          </>
        }
      />

      {/* Folded on a first visit, like the Details of a case or a checklist:
          the properties are reference material while the execution below is
          what the page is opened for. */}
      <CollapsibleCard title={t('cases.details')} rememberAs="runDetails">
        <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('cases.columnKey')}
            </dt>
            <dd className="mt-1">
              <CopyKey path={`/projects/${projectCode}/test-runs/${run.runKey}`}>
                {run.runKey}
              </CopyKey>
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.status')}
            </dt>
            <dd className="mt-1">
              <RunStatusBadge status={run.status} />
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('labels.title')}
            </dt>
            <dd className="mt-1">
              {run.labels.length === 0 ? (
                <span className="text-[13px] text-ink-subtle">{t('common.empty')}</span>
              ) : (
                <span className="flex flex-wrap gap-1">
                  {run.labels.map((label) => (
                    <LabelChip key={label.id} label={label} />
                  ))}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.environment')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">
              {run.environment?.name ?? t('environments.none')}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('runs.metaAssignee')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">
              {run.assignee?.name ?? t('common.unassigned')}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.created')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">{dateTime(run.createdAt)}</dd>
          </div>
          {run.completedAt ? (
            <div>
              <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
                {t('runs.metaCompleted')}
              </dt>
              <dd className="mt-1 text-[13px] text-ink">{dateTime(run.completedAt)}</dd>
            </div>
          ) : null}
        </dl>

        {/* The sources and the written fields sit in the same folded block as
            the properties: three cards of their own took the whole screen
            before the execution below could start. A rule keeps them apart. */}
        <div className="mt-4 border-t border-border pt-4">
          <p className="text-[12px] uppercase tracking-wide text-ink-subtle">
            {t('runs.sourceChecklists', { count: run.checklists.length })}
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {run.checklists.map((source, index) => (
              <li key={`${source.id ?? 'deleted'}-${index}`} className="text-[13px]">
                {source.key ? (
                  <Link
                    to="/projects/$projectCode/checklists/$checklistKey"
                    params={{ projectCode, checklistKey: source.key }}
                    className="font-medium text-accent-fg hover:underline"
                  >
                    {source.title}
                  </Link>
                ) : (
                  <span className="text-ink">
                    {t('runs.checklistDeleted', { title: source.title })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 grid gap-4 border-t border-border pt-4 sm:grid-cols-2">
          <div>
            <p className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.description')}
            </p>
            {run.description ? (
              <RichText value={run.description} className="mt-1" />
            ) : (
              <p className="mt-1 text-[13px] text-ink-subtle">{t('common.empty')}</p>
            )}
          </div>
          <div>
            <p className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.comment')}
            </p>
            {run.comment ? (
              <RichText value={run.comment} className="mt-1" />
            ) : (
              <p className="mt-1 text-[13px] text-ink-subtle">{t('common.empty')}</p>
            )}
          </div>
        </div>
      </CollapsibleCard>

      <Card>
        <CardBody className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <ProgressBar
              value={run.stats.progress}
              className="flex-1"
              label={t('runs.runProgress')}
            />
            <span className="w-32 text-right text-[13px] font-medium text-ink">
              {t('runs.progressSummary', {
                progress: run.stats.progress,
                executed: run.stats.executed,
                total: run.stats.total,
              })}
            </span>
          </div>

          <ResultBar
            passed={run.stats.passed}
            failed={run.stats.failed}
            blocked={run.stats.blocked}
            skipped={run.stats.skipped}
            total={run.stats.total}
          />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            <Stat
              label={t('runs.statPassRate')}
              value={`${run.stats.passRate}%`}
              valueClassName={run.stats.failed > 0 ? 'text-fail' : 'text-pass'}
            />
            <Stat label={t('runs.statPassed')} value={run.stats.passed} valueClassName="text-pass" />
            <Stat label={t('runs.statFailed')} value={run.stats.failed} valueClassName="text-fail" />
            <Stat
              label={t('runs.statBlocked')}
              value={run.stats.blocked}
              valueClassName="text-block"
            />
            <Stat
              label={t('runs.statSkipped')}
              value={run.stats.skipped}
              valueClassName="text-skip"
            />
            <Stat
              label={t('runs.statNotRun')}
              value={run.stats.not_run}
              valueClassName="text-notrun"
            />
            <Stat label={t('runs.statTotal')} value={run.stats.total} />
          </div>

          {failed.length > 0 ? (
            <div className="rounded-lg border border-fail/30 bg-fail-soft/50 px-3 py-2.5">
              <p className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-fail">
                {t('runs.failedChecks')}
              </p>
              <ul className="flex flex-col gap-0.5">
                {failed.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() =>
                        select(run.items.findIndex((candidate) => candidate.id === item.id))
                      }
                      className="text-left text-[13px] text-ink hover:underline"
                    >
                      {item.position}. {toPlainText(item.title)}
                      {item.comment ? (
                        <span className="text-ink-muted"> — {toPlainText(item.comment)}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {automation ? (
        <RunAutotests
          projectCode={projectCode}
          run={run}
          automation={automation}
          canStart={canStartAutotests}
        />
      ) : null}

      <Card>
        <CardHeader
          title={t('runs.checksCount', { count: run.items.length })}
          description={
            editable
              ? t('runs.checksHintEditable')
              : isActive
                ? t('runs.checksHintNoPermission')
                : t('runs.checksHintCompleted')
          }
          action={
            editable && remaining.length > 0 ? (
              <Button size="sm" onClick={() => void skipRemaining()} loading={bulkStatus.isPending}>
                <SkipForward className="size-3.5" />
                {t('runs.skipRemaining', { count: remaining.length })}
              </Button>
            ) : null
          }
        />

        <ul
          className="max-h-[22rem] divide-y divide-border overflow-y-auto"
          aria-label={t('runs.checksAria')}
        >
          {run.items.map((item, index) => (
            <li
              key={item.id}
              ref={(element) => {
                if (element) rowRefs.current.set(item.id, element);
                else rowRefs.current.delete(item.id);
              }}
              aria-current={current?.id === item.id ? 'true' : undefined}
              className={cn(
                'flex items-center gap-2 px-4 py-2 transition-colors',
                current?.id === item.id ? 'bg-accent-soft/50' : 'hover:bg-canvas',
              )}
            >
              <button
                type="button"
                onClick={() => select(index)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <span className="w-6 shrink-0 text-[12px] tabular-nums text-ink-subtle">
                  {item.position}
                </span>
                {/* The badge is the status here: the panel below no longer
                    repeats it, and an icon beside it would say it twice. */}
                <span className="w-36 shrink-0">
                  <RunItemStatusBadge status={item.status} />
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                  {toPlainText(item.title)}
                </span>
                {autotested.has(item.id) ? (
                  <Bot
                    className="size-3.5 shrink-0 text-accent-fg"
                    role="img"
                    aria-label={t('runs.autotests.badge')}
                  />
                ) : null}
                {item.comment ? (
                  <MessageSquare
                    className="size-3.5 shrink-0 text-ink-subtle"
                    aria-label={t('runs.hasComment')}
                  />
                ) : null}
              </button>
              {item.executedAt ? (
                // What was recorded belongs to the check, not to the panel
                // below: every row says who recorded it, when, and leads to
                // that check's own history.
                <span className="hidden shrink-0 items-center gap-2 text-[12px] text-ink-subtle sm:flex">
                  <span>
                    {t('runs.executedBy', {
                      name: item.executedBy?.name ?? t('common.empty'),
                      date: dateTime(item.executedAt),
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setHistoryItem(item)}
                    className="inline-flex items-center gap-1 text-accent-fg hover:underline"
                  >
                    <History className="size-3" />
                    {t('runs.history')}
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>

        {current ? (
          <div
            role="group"
            aria-label={t('runs.currentCheckAria')}
            className="border-t border-border bg-canvas px-4 py-3"
          >
            {/* The result comes first: a long check — or one with screenshots —
                must not push the buttons off the screen. */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {editable ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-[12px] font-medium text-ink-muted">
                    {t('runs.result')}
                  </span>
                  {STATUS_ACTIONS.map((action) => (
                    <button
                      key={action.status}
                      type="button"
                      onClick={() => void apply(current, action.status, true)}
                      aria-pressed={current.status === action.status}
                      className={cn(
                        'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium transition-all',
                        action.className,
                        current.status === action.status
                          ? 'ring-2 ring-current/40'
                          : 'opacity-80 hover:opacity-100',
                      )}
                    >
                      <RunItemStatusIcon status={action.status} className="size-3.5" />
                      {itemStatus(action.status)}
                    </button>
                  ))}
                </div>
              ) : (
                <span />
              )}

              <div className="flex shrink-0 items-center gap-1.5">
                <Button
                  size="sm"
                  onClick={() => select(Math.max(selected - 1, 0))}
                  disabled={selected === 0}
                  aria-label={t('runs.previousCheck')}
                >
                  <ChevronLeft className="size-3.5" />
                  {t('common.previous')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => select(Math.min(selected + 1, run.items.length - 1))}
                  disabled={selected >= run.items.length - 1}
                  aria-label={t('runs.nextCheck')}
                >
                  {t('common.next')}
                  <ChevronRight className="size-3.5" />
                </Button>
              </div>
            </div>

            <div className="mt-3 min-w-0">
              <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
                {t('runs.currentCheck', {
                  position: current.position,
                  total: run.items.length,
                })}
              </p>
              <RichText value={current.title} className="mt-1 text-[14px] font-medium text-ink" />
              {current.expectedResult ? (
                // The expected result is written in the checklist with the same
                // Markdown as a test case, so it is rendered, not shown as markup.
                <div className="mt-1">
                  <p className="text-[12px] uppercase tracking-wide text-ink-subtle">
                    {t('runs.expectedLabel')}
                  </p>
                  <RichText value={current.expectedResult} className="mt-0.5 max-w-5xl" />
                </div>
              ) : null}
            </div>


            {editable ? (
              <div className="mt-3 flex flex-col gap-2">
                {/* Not a <label>: the editor is a toolbar around the field, and
                    clicking the toolbar must not count as clicking the label. */}
                <div className="flex flex-col gap-1">
                  <p className="text-[12px] font-medium text-ink">{t('common.comment')}</p>
                  <RichTextEditor
                    rows={3}
                    projectCode={projectCode}
                    value={currentComment}
                    onChange={(value) =>
                      setComments((draft) => ({ ...draft, [current.id]: value }))
                    }
                    placeholder={t('runs.commentPlaceholder')}
                    aria-label={t('runs.commentAria', { position: current.position })}
                  />
                </div>

                {current.status !== 'not_run' && commentChanged ? (
                  <div>
                    <Button
                      size="sm"
                      onClick={() => void apply(current, current.status)}
                      loading={setStatus.isPending}
                    >
                      <Save className="size-3.5" />
                      {t('runs.saveComment')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : current.comment ? (
              <div className="mt-3 rounded-md bg-surface px-3 py-2">
                <RichText value={current.comment} />
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      <RunItemHistoryDialog
        projectCode={projectCode}
        runId={run.id}
        item={historyItem}
        onClose={() => setHistoryItem(null)}
      />

      <EditRunDialog open={editOpen} onOpenChange={setEditOpen} projectCode={projectCode} run={run} />

      <ConfirmDialog
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        title={t('runs.completeTitle', { key: run.runKey })}
        description={
          remaining.length > 0
            ? t('runs.completeHintRemaining', {
                checks: t('runs.countChecks', { count: remaining.length }),
              })
            : t('runs.completeHint')
        }
        confirmLabel={t('runs.complete')}
        destructive={false}
        loading={complete.isPending}
        onConfirm={() => void onComplete()}
      />
    </>
  );
};
